<template>
  <div>
    <b-button class="pull-right" v-if="isAdmin" :to="{ path: '/impersonation/' + user.asuriteId }"
      variant="primary">Click
      to
      impersonate</b-button>

    <h3>{{ user.asuriteId }}</h3>

    <router-link :to="{ name: 'users' }" class="return-link">Return to Users List</router-link>
    <b-form @submit.prevent="save">
      <b-container class="user-main-container">
        <b-row>
          <b-col sm="3">
            <span class="user-detail-label">Basic Information</span>
            <b-row>
              <b-col sm="4" class="align-right">
                <label for="first-name" :class="{
                  'user-detail-label': editing,
                  bolded: !editing,
                  'invalid-label': $v.form.firstName.$error,
                }">First Name:</label>
              </b-col>
              <b-col sm="8" class="align-left">
                <b-form-input id="first-name" v-model="form.firstName" :class="{
                  'text-input': true,
                  'is-invalid': $v.form.firstName.$error,
                }" v-if="editing"></b-form-input>
                <span v-if="!editing">{{ user.firstName }}</span>
                <span class="span-feedback invalid-feedback" v-if="$v.form.firstName.$error">Please enter a first
                  name.</span>
              </b-col>
            </b-row>
            <b-row>
              <b-col sm="4" class="align-right">
                <label for="last-name" :class="{
                  'user-detail-label': editing,
                  bolded: !editing,
                  'invalid-label': $v.form.lastName.$error,
                }">Last Name:</label>
              </b-col>
              <b-col sm="8" class="align-left">
                <b-form-input id="last-name" v-model="form.lastName" :class="{
                  'text-input': true,
                  'is-invalid': $v.form.lastName.$error,
                }" v-if="editing"></b-form-input>
                <span v-if="!editing">{{ user.lastName }}</span>
                <span class="span-feedback invalid-feedback" v-if="$v.form.lastName.$error">Please enter a last
                  name.</span>
              </b-col>
            </b-row>
            <b-row>
              <b-col sm="4" class="align-right">
                <label for="user-email" :class="{
                  'user-detail-label': editing,
                  bolded: !editing,
                  'invalid-label': $v.form.email.$error,
                }">Email:</label>
              </b-col>
              <b-col sm="8" class="align-left">
                <b-form-input id="user-email" v-model="form.email" :class="{
                  'text-input': true,
                  'is-invalid': $v.form.email.$error,
                }" type="email" v-if="editing"></b-form-input>
                <span v-if="!editing">{{ user.email }}</span>
                <span class="span-feedback invalid-feedback" v-if="$v.form.email.$error">Please enter a valid email
                  address.</span>
              </b-col>
            </b-row>
            <b-row>
              <b-col sm="4" class="align-right">
                <label for="user-preventroleadditions" :class="{
                  'user-detail-label': editing,
                  bolded: !editing
                }">Prevent automated role additions:</label>
              </b-col>
              <b-col sm="8" class="align-left">
                <b-form-checkbox v-if="editing" class="pull-right" v-model="form.preventRoleAdditions">
                </b-form-checkbox>
                <span v-if="!editing">{{ user.preventRoleAdditions | trueOrFalse }}</span>

              </b-col>
            </b-row>
            <b-row>
              <b-col sm="4" class="align-right">
                <label for="user-preventroleremoval" :class="{
                  'user-detail-label': editing,
                  bolded: !editing
                }">Prevent automated role removals:</label>
              </b-col>
              <b-col sm="8" class="align-left">
                <b-form-checkbox v-if="editing" class="pull-right" v-model="form.preventRoleRemoval">
                </b-form-checkbox>
                <span v-if="!editing">{{ user.preventRoleRemoval | trueOrFalse }}</span>

              </b-col>
            </b-row>

            <b-row>
              <!-- basic form for suporg text field and inherit checkbox -->
              <b-col sm="4" class="align-right">
                <label for="user-suporg" :class="{
                  'user-detail-label': editing,
                  bolded: !editing
                }">Sup orgs:</label>
              </b-col>
              <b-col sm="8" class="align-left">
                <div v-if="editing">
                  <!-- <b-form-input v-model="newSupOrg" placeholder="Add new sup org"></b-form-input> -->
                  <autocomplete ref="suporgAutocomplete" :source="deptSearch" credentials="include" resultsValue="deptId"
                    :resultsDisplay="supOrgDisplay" @selected="setSupOrg" @clear="clearSupOrg"
                    :request-headers="requestHeaders" id="department-select" :inputClass="{
                      'form-control': true,
                      'text-input': true,
                      'autocomplete-input': true,
                    }">
                  </autocomplete>
                  <b-form-checkbox v-model="newSupOrgInherit" class="ml-2">
                    Inherit
                  </b-form-checkbox>
                  <b-button @click="addSupOrg(newSupOrg)" :disabled="!newSupOrg">
                    <font-awesome-icon icon="plus" />
                  </b-button>
                </div>
                <div v-for="userSupOrg in form.userSupOrgs" :key="userSupOrg.supOrg" class="mb-2">
                    <b-badge class="mr-2">{{ userSupOrg.supOrg }} <span v-if="userSupOrg.inherit">(inherit)</span></b-badge>
                    <b-button size="sm" class="ml-2" @click="removeSupOrg(userSupOrg)" v-if="editing">
                      <font-awesome-icon icon="times" />
                    </b-button>
                </div>
              </b-col>
            </b-row>
          </b-col>
          <b-col sm="3">
            <span class="user-detail-label">Roles</span>
            <div v-for="role in roles" :key="role.roleId">
              <b-badge :class="{
                'clickable-badge': editing,
                'unselected-badge': !hasRole(role),
              }" @click="toggleRoleSelection(role)" v-if="editing || hasRole(role)">
                {{ role.name }}
                <font-awesome-icon :icon="hasRole(role) ? 'times' : 'plus'" v-if="editing" />
              </b-badge>
            </div>
          </b-col>
          <b-col sm="3">
            <span class="user-detail-label">Colleges</span>
            <div v-for="college in colleges" :key="college.collegeId">
              <b-badge :class="{
                'clickable-badge': editing,
                'unselected-badge': !hasCollege(college),
              }" @click="toggleCollegeSelection(college)" v-if="editing || hasCollege(college)">
                {{ college.name }}
                <font-awesome-icon :icon="hasCollege(college) ? 'times' : 'plus'" v-if="editing" />
              </b-badge>
            </div>
          </b-col>
          <b-col sm="3">
            <span class="user-detail-label">Academic Plans</span>
            <div v-if="editing">
              <b-button size="sm" v-if="
                academicPlans.length > 0 &&
                academicPlans.length > form.academicPlans.length
              " @click="selectAllAcademicPlans()">
                <font-awesome-icon icon="plus" /> Select All
              </b-button>
              <b-button size="sm" v-if="
                academicPlans.length > 0 &&
                academicPlans.length == form.academicPlans.length
              " @click="deselectAllAcademicPlans()">
                <font-awesome-icon icon="times" /> Deselect All
              </b-button>
            </div>
            <b-input-group class="filter-group" v-if="form.colleges.length > 0 && editing">
              <b-input-group-prepend is-text>
                <font-awesome-icon icon="search" />
              </b-input-group-prepend>
              <b-form-input v-model="filter" placeholder="Search"></b-form-input>
              <b-input-group-append>
                <b-button @click="filter = null">Clear</b-button>
              </b-input-group-append>
            </b-input-group>
            <div v-for="college in form.colleges" :key="college.collegeId">
              <p class="college-header">{{ college.name }}</p>
              <div v-for="academicPlan in academicPlansByCollege[
                college.collegeId
              ]" :key="academicPlan.academicPlanId">
                <b-badge :class="{
                  'clickable-badge': editing,
                  'unselected-badge': !hasAcademicPlan(academicPlan),
                }" @click="toggleAcademicPlanSelection(academicPlan)" v-if="editing || hasAcademicPlan(academicPlan)">
                  {{ academicPlan.description }}
                  <font-awesome-icon :icon="hasAcademicPlan(academicPlan) ? 'times' : 'plus'" v-if="editing" />
                </b-badge>
              </div>
            </div>
          </b-col>
        </b-row>
        <b-button size="sm" @click="editing = true" v-if="!editing" class="user-editing-button"><font-awesome-icon
            icon="edit" /> Edit</b-button>
        <b-button size="sm" type="submit" v-if="editing" class="user-editing-button"><font-awesome-icon icon="check" />
          Save changes</b-button>
        <b-button size="sm" @click="cancel()" v-if="editing" class="user-editing-button"><font-awesome-icon
            icon="times" /> Cancel</b-button>
      </b-container>
    </b-form>
  </div>
</template>

<script>
import { required, email } from "vuelidate/lib/validators";
import { mapState } from "vuex";
import toastr from "toastr";
import usersApi from "../../api/users";

export default {
  data() {
    return {
      form: {
        firstName: null,
        lastName: null,
        email: null,
        roles: [],
        colleges: [],
        academicPlans: [],
        userSupOrgs: [],
        preventRoleAdditions: false,
        preventRoleRemoval: false,
      },
      selectedSuporg: null,
      newSupOrg: null,
      newSupOrgInherit: false,
      editing: false,
      filter: null,
    };
  },

  validations: {
    form: {
      firstName: {
        required,
      },
      lastName: {
        required,
      },
      email: {
        required,
        email,
      },
    },
  },

  computed: {
    ...mapState({
      currentUser: (state) => state.auth.user,
    }),

    deptSearch: function() {
      return process.env.VUE_APP_BASE_URI + "/api/Departments/search?query=";
    },

    isAdmin: function() {
      return _.find(this.currentUser.userRoles, function(userRole) {
        return userRole === "Impersonation";
      });
    },
    academicPlans: function() {
      var that = this;
      var academicPlanList = [];
      _.forEach(this.form.colleges, function(college) {
        _.forEach(college.academicPlans, function(academicPlan) {
          if (
            !that.filter ||
            academicPlan.description
              .toLowerCase()
              .includes(that.filter.toLowerCase())
          ) {
            academicPlanList.push(academicPlan);
          }
        });
      });
      return academicPlanList;
    },

    academicPlansByCollege: function() {
      // group by collegeId
      var groupedAcademicPlans = _.groupBy(this.academicPlans, "collegeId");

      // alphabetize academicPlans
      _.forEach(groupedAcademicPlans, function(group, collegeId) {
        group = _.sortBy(group, ["description"]);
        groupedAcademicPlans[collegeId] = group;
      });

      return groupedAcademicPlans;
    },
  },

  methods: {
    setSupOrg: function(selectedDepartment) {
      this.newSupOrg = selectedDepartment.value;
    },
    supOrgDisplay: function(supOrg) {
      return "(" + supOrg.deptId + ") " + supOrg.description;
    },
    clearSupOrg: function() {
      this.newSupOrg = null;
      this.$refs.suporgAutocomplete.display = "";
    },
    save: function() {
      var that = this;
      this.user.firstName = this.form.firstName;
      this.user.lastName = this.form.lastName;
      this.user.email = this.form.email;
      this.user.preventRoleAdditions = this.form.preventRoleAdditions;
      this.user.preventRoleRemoval = this.form.preventRoleRemoval;

      this.user.userRoles = [];
      _.forEach(this.form.roles, function(role) {
        var newUserRole = {
          asuriteId: that.user.asuriteId,
          role: role,
          roleId: role.roleId,
        };
        that.user.userRoles.push(newUserRole);
      });

      this.user.userColleges = [];
      _.forEach(this.form.colleges, function(college) {
        var newUserCollege = {
          asuriteId: that.user.asuriteId,
          college: college,
          collegeId: college.collegeId,
        };
        that.user.userColleges.push(newUserCollege);
      });

      this.user.userAcademicPlans = [];
      _.forEach(this.form.academicPlans, function(academicPlan) {
        var newUserAcademicPlan = {
          asuriteId: that.user.asuriteId,
          academicPlan: academicPlan,
          academicPlanId: academicPlan.academicPlanId,
        };
        that.user.userAcademicPlans.push(newUserAcademicPlan);
      });

      this.user.userSupOrgs = [];
      _.forEach(this.form.userSupOrgs, function(userSupOrg) {
        var newUserSupOrg = {
          asuriteId: that.user.asuriteId,
          supOrg: userSupOrg.supOrg,
          inherit: userSupOrg.inherit,
        };
        that.user.userSupOrgs.push(newUserSupOrg);
      });

      usersApi
        .updateUser(this.user)
        .then(function() {
          toastr.options = {
            closeButton: true,
            debug: false,
            newestOnTop: false,
            progressBar: false,
            positionClass: "toast-top-right",
            preventDuplicates: false,
            onclick: null,
            showDuration: "300",
            hideDuration: "0",
            timeOut: "5000",
            extendedTimeOut: "1000",
            showEasing: "swing",
            hideEasing: "linear",
            showMethod: "fadeIn",
            hideMethod: "fadeOut",
          };

          // Toastr Message
          toastr.success("Changes saved.", "Success");
          that.editing = false;
          that.filter = null;
        })
        .catch(function(error) {
          console.log(error);
        });
    },

    cancel: function() {
      var that = this;
      // Revert user to the user copy from before we started editing
      this.form.firstName = this.user.firstName;
      this.form.lastName = this.user.lastName;
      this.form.email = this.user.email;
      this.form.preventRoleAdditions = this.user.preventRoleAdditions;
      this.form.preventRoleRemoval = this.user.preventRoleRemoval;

      this.form.roles = [];
      _.forEach(this.user.userRoles, function(userRole) {
        that.form.roles.push(userRole.role);
      });

      this.form.colleges = [];
      _.forEach(this.user.userColleges, function(userCollege) {
        that.form.colleges.push(userCollege.college);
      });

      this.form.userAcademicPlans = [];
      _.forEach(this.user.userAcademicPlans, function(userAcademicPlan) {
        that.form.academicPlans.push(userAcademicPlan.academicPlan);
      });

      this.form.userSupOrgs = [];
      _.forEach(this.user.userSupOrgs, function(userSupOrg) {
        that.form.userSupOrgs.push(userSupOrg);
      });

      this.editing = false;
    },

    // check if this role has already been added to the user
    hasRole: function(role) {
      return _.find(this.form.roles, function(addedRole) {
        return addedRole.roleId === role.roleId && addedRole.app === role.app;
      });
    },

    hasCollege: function(college) {
      return _.find(this.form.colleges, function(addedCollege) {
        return addedCollege.collegeId === college.collegeId;
      });
    },

    hasAcademicPlan: function(academicPlan) {
      return _.find(this.form.academicPlans, function(addedAcademicPlan) {
        return addedAcademicPlan.academicPlanId === academicPlan.academicPlanId;
      });
    },

    toggleRoleSelection: function(role) {
      if (this.editing) {
        if (this.hasRole(role)) {
          this.form.roles = _.reject(this.form.roles, function(addedRole) {
            return (
              addedRole.roleId === role.roleId && addedRole.app === role.app
            );
          });
        } else {
          this.form.roles.push(role);
        }
      }
    },

    toggleCollegeSelection: function(college) {
      var that = this;
      if (this.editing) {
        if (this.hasCollege(college)) {
          this.form.colleges = _.reject(this.form.colleges, function(
            addedCollege,
          ) {
            return addedCollege.collegeId === college.collegeId;
          });
          // remove academic plans for this college as well
          _.forEach(this.form.academicPlans, function(academicPlan) {
            if (academicPlan.collegeId === college.collegeId) {
              that.toggleAcademicPlanSelection(academicPlan);
            }
          });
        } else {
          this.form.colleges.push(college);
        }
      }
    },

    toggleAcademicPlanSelection: function(academicPlan) {
      if (this.editing) {
        if (this.hasAcademicPlan(academicPlan)) {
          this.form.academicPlans = _.reject(this.form.academicPlans, function(
            addedAcademicPlan,
          ) {
            return (
              addedAcademicPlan.academicPlanId === academicPlan.academicPlanId
            );
          });
        } else {
          this.form.academicPlans.push(academicPlan);
        }
      }
    },

    selectAllAcademicPlans: function() {
      var that = this;
      _.forEach(this.academicPlans, function(academicPlan) {
        if (!that.hasAcademicPlan(academicPlan)) {
          that.form.academicPlans.push(academicPlan);
        }
      });
    },

    deselectAllAcademicPlans: function() {
      this.form.academicPlans = [];
    },

    addSupOrg: function() {
      var supOrg = this.newSupOrg;
      var inherit = this.newSupOrgInherit;
      if (
        !this.form.userSupOrgs.some(function(userSupOrg) {
          return userSupOrg.supOrg === supOrg;
        })
      ) {
        this.form.userSupOrgs.push({
          supOrg: supOrg,
          inherit: inherit,
        });
      }
      this.newSupOrg = null;
      this.newSupOrgInherit = false;

      this.clearSupOrg();
    },

    removeSupOrg: function(userSupOrg) {
      this.form.userSupOrgs = _.reject(this.form.userSupOrgs, function(supOrg) {
        return supOrg.supOrg === userSupOrg.supOrg;
      });
    },
  },

  props: {
    user: {
      required: true,
    },

    roles: {
      type: Array,
      required: true,
    },

    colleges: {
      type: Array,
      required: true,
    },
  },

  created() {
    var that = this;
    this.form.firstName = this.user.firstName;
    this.form.lastName = this.user.lastName;
    this.form.email = this.user.email;
    this.form.preventRoleAdditions = this.user.preventRoleAdditions;
    this.form.preventRoleRemoval = this.user.preventRoleRemoval;
    _.forEach(this.user.userRoles, function(userRole) {
      that.form.roles.push(userRole.role);
    });
    _.forEach(this.user.userColleges, function(userCollege) {
      that.form.colleges.push(userCollege.college);
    });
    _.forEach(this.user.userAcademicPlans, function(userAcademicPlan) {
      that.form.academicPlans.push(userAcademicPlan.academicPlan);
    });
    _.forEach(this.user.userSupOrgs, function(userSupOrg) {
      that.form.userSupOrgs.push(userSupOrg);
    });
  },
};
</script>

<style scoped>
.user-main-container {
  max-width: 100%;
  text-align: center;
}

.align-right {
  text-align: right;
}

.align-left {
  text-align: left;
}

.invalid-label {
  color: #dc3545;
}

.user-detail-label {
  font-weight: bold;
  line-height: 38px;
}

.clickable-badge {
  cursor: pointer;
}

.unselected-badge {
  opacity: 0.5;
}

.college-header {
  text-decoration: underline;
  margin-bottom: 0;
  font-weight: bold;
}

.filter-group {
  margin: 10px 0 15px 0;
}

.user-editing-button {
  margin-left: 2px;
  margin-right: 2px;
}
</style>
